/**
 * What the Publish workspace and the Export dialog use of the problems service (W2-09 publish-import): whether a
 * checker pass is still running (`ProblemsService.checking`, CR-W2-09-6) and waiting for it.
 */

import type { AppServices } from '../../app/registry/types';
import type { Unsubscribe } from '../../core/util/types';

/**
 * A checker pass that covers the latest changes has not finished yet: problems only the checker computes (PH-03 text
 * panic, PH-04 zero-delay GIF frames, PH-05 file names, PH-06, PH-07...) may still be missing from the list. A
 * failed analysis worker or a checker without an open chart never counts as checking (nothing would end it).
 */
export function checkRunning(problems: AppServices['problems']): boolean {
  try {
    return problems.checking();
  } catch {
    return false;
  }
}

/**
 * How long an export waits for a running checker pass before asking whether to export anyway. Short enough that the
 * browser save dialog that follows still counts as opened by the user's click or key press (Chromium allows about
 * five seconds).
 */
export const CHECK_WAIT_MS = 2500;

/**
 * Waits until the running checker pass has finished: true when it did (or none was running), false after
 * `timeoutMs`, or when `cancelled` turns true (checked on every problems change and at the end).
 */
export function waitForCheck(
  problems: AppServices['problems'],
  timeoutMs: number = CHECK_WAIT_MS,
  cancelled: () => boolean = () => false,
): Promise<boolean> {
  if (!checkRunning(problems)) return Promise.resolve(true);
  return new Promise<boolean>((resolve) => {
    // An object, not a local boolean: it changes inside callbacks the flow analysis does not follow.
    const state = { settled: false };
    let off: Unsubscribe | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const finish = (done: boolean): void => {
      if (state.settled) return;
      state.settled = true;
      off?.();
      if (timer !== null) clearTimeout(timer);
      resolve(done && !cancelled());
    };
    off = problems.subscribe(() => {
      if (cancelled()) finish(false);
      else if (!checkRunning(problems)) finish(true);
    });
    if (state.settled) {
      // Settled while subscribing (before `off` was assigned).
      off();
      return;
    }
    // The pass may have ended between the first look and the subscription.
    if (!checkRunning(problems)) {
      finish(true);
      return;
    }
    timer = setTimeout(() => {
      finish(!checkRunning(problems));
    }, timeoutMs);
  });
}
